import PropTypes from 'prop-types';
import React from 'react';
import lodash from 'lodash';
import moment from 'moment';

import { Col, FormControl, FormGroup, Row } from 'components/graylog';
import { Input } from 'components/bootstrap';
import { Icon, Select } from 'components/common';
import DateTime from 'logic/datetimes/DateTime';
import TimezoneSelect from 'components/common/TimezoneSelect';

import TimeSelect from './TimeSelect';
import DayOfWeekSelect from './DayOfWeekSelect';
import DayOfMonthSelect from './DayOfMonthSelect';

const FREQUENCY_VALUES = ['daily', 'weekly', 'monthly'];
export const FREQUENCY_OPTIONS = {
  daily: {
    value: 'daily',
    label: 'Daily',
  },
  weekly: {
    value: 'weekly',
    label: 'Weekly',
  },
  monthly: {
    value: 'monthly',
    label: 'Monthly',
  },
};

class RecurringEventControl extends React.Component {
  static propTypes = {
    disabled: PropTypes.bool,
    frequency: PropTypes.string,
    frequency_configuration: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
  };

  static defaultProps = {
    disabled: false,
    frequency: undefined,
  };

  _propagateChanges = (frequency, configuration) => {
    const update = {
      frequency: frequency,
      frequency_configuration: configuration,
    };

    this.props.onChange(update);
  };

  _handleFrequencyChange = (value) => {
    this._propagateChanges(value, {
      timezone: DateTime.getUserTimezone(),
    });
  };

  _handleFrequencyConfigurationChange = (name) => {
    return (value) => {
      const { frequency_configuration } = this.props;
      const updatedConfiguration = { ...frequency_configuration, [name]: value };

      this._propagateChanges(this.props.frequency, updatedConfiguration);
    };
  };

  _getUTCTime = (time, timezone) => {
    if (!time) {
      return time;
    }

    const [hours, minutes] = time.split(':');
    const dateTime = new DateTime().toTimeZone(timezone ?? 'UTC').toMoment()
      .hours(hours)
      .minutes(minutes);

    return dateTime.utc().format('HH:mm');
  };

  _formatTime = (time, timezone) => {
    return `${time} local time (${this._getUTCTime(time, timezone)} UTC)`;
  };

  _renderFrequencyConfiguration = (frequency, disabled, frequencyConfiguration) => {
    let summary = 'No options selected.';
    let helpText;
    let controls;
    let timeControl;

    if (frequencyConfiguration) {
      const { time } = frequencyConfiguration;

      const timezone = frequencyConfiguration.timezone ?? 'UTC';

      timeControl = (
        <>
          <Col sm={4}>
            <TimeSelect id="time"
                        value={time}
                        disabled={disabled}
                        required
                        onChange={this._handleFrequencyConfigurationChange('time')} />
          </Col>
          <Col sm={4}>
            <TimezoneSelect value={timezone}
                            disabled={disabled}
                            clearable={false}
                            onChange={this._handleFrequencyConfigurationChange('timezone')} />
          </Col>
        </>
      );
    }

    switch (frequency) {
      case FREQUENCY_OPTIONS.daily.value:
        if (frequencyConfiguration.time) {
          summary = `Report will be sent every day at ${this._formatTime(frequencyConfiguration.time, frequencyConfiguration.timezone)}.`;
        }

        helpText = 'Select the time of the day when the report should be sent out.';

        controls = (
          <Row>
            {timeControl}
          </Row>
        );

        break;
      case FREQUENCY_OPTIONS.weekly.value:
        if (frequencyConfiguration.day_of_week && frequencyConfiguration.time) {
          summary = `Report will be sent every ${lodash.upperFirst(frequencyConfiguration.day_of_week)} at ${this._formatTime(frequencyConfiguration.time, frequencyConfiguration.timezone)}.`;
        }

        helpText = 'Select the day of the week and time of the day when the report should be sent out.';

        controls = (
          <Row>
            <Col sm={4}>
              <DayOfWeekSelect id="day_of_week"
                               value={frequencyConfiguration.day_of_week}
                               disabled={disabled}
                               required
                               onChange={this._handleFrequencyConfigurationChange('day_of_week')} />
            </Col>
            {timeControl}
          </Row>
        );

        break;
      case FREQUENCY_OPTIONS.monthly.value:
        if (frequencyConfiguration.day_of_month && frequencyConfiguration.time) {
          const ordinalDay = moment.localeData().ordinal(lodash.upperFirst(frequencyConfiguration.day_of_month));
          summary = `Report will be sent the ${ordinalDay} day of every month at ${this._formatTime(frequencyConfiguration.time, frequencyConfiguration.timezone)}.`;
        }

        helpText = 'Select the day of the month and time of the day when the report should be sent out.';

        controls = (
          <Row>
            <Col sm={4}>
              <DayOfMonthSelect id="day_of_month"
                                value={frequencyConfiguration.day_of_month}
                                disabled={disabled}
                                required
                                onChange={this._handleFrequencyConfigurationChange('day_of_month')} />
            </Col>
            {timeControl}
          </Row>
        );

        break;
      default:
        return <div />;
    }

    return (
      <Input id="frequency-options-preview" label={`${FREQUENCY_OPTIONS[frequency].label} frequency options`} help={helpText}>
        <div>
          <FormControl.Static className="text-info"><Icon name="info-circle" /> {summary}</FormControl.Static>
          <FormGroup>
            {controls}
          </FormGroup>
        </div>
      </Input>
    );
  };

  render() {
    const { disabled, frequency } = this.props;
    const frequencyConfiguration = this.props.frequency_configuration;
    const frequencyOptions = FREQUENCY_VALUES.map((value) => FREQUENCY_OPTIONS[value]);

    return (
      <div>
        <Input id="frequency-select" label="Frequency" help="Select how often this report should be sent out.">
          <Select id="frequency"
                  options={frequencyOptions}
                  value={frequency}
                  placeholder="Select a frequency"
                  disabled={disabled}
                  required
                  onChange={this._handleFrequencyChange} />
        </Input>
        {this._renderFrequencyConfiguration(frequency, disabled, frequencyConfiguration)}
      </div>
    );
  }
}

export default RecurringEventControl;
